// Sandbox ("Labs") mode — a per-device switch that lets you use the whole app on
// a real account without writing anything: reads are live, the UI updates
// optimistically exactly as it would for real, and nothing reaches the database,
// Storage, or a Netlify function that would persist or notify. A reload puts
// truth back.
//
// Started life (2026-08) as an opt-in wrapper (labsWrite) that a few features
// adopted one write at a time. Widened 2026-09-20 (Jason: "a toggle on that fab
// button that lets me interact with the app but does not write to the database
// ... test features without affecting my personal data") into a global gate:
//   • lib/supabase.js wraps the client so insert/update/upsert/delete, write-
//     shaped RPCs and Storage uploads all short-circuit while this is on.
//   • installSandboxGuard() (main.jsx) short-circuits the Netlify functions that
//     write or push — notify-activity would otherwise still ping followers.
//   • labsWrite() is kept for the call sites that already use it; it's now
//     redundant with the client gate but harmless, and still logs the label.
// The flag is plain localStorage (synchronous, no schema, no round-trip) so any
// module can read it on the hot path. It PERSISTS across reloads on purpose —
// a test session spans reloads — so the AdminFab wears a visible SANDBOX badge
// the whole time it's on.

const KEY = 'nextep:labs'
const EVENT = 'nextep:labs-changed'

export function isLabsMode() {
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

export function setLabsMode(on) {
  try {
    if (on) localStorage.setItem(KEY, '1')
    else localStorage.removeItem(KEY)
  } catch { /* ignore (private mode, storage disabled, etc.) */ }
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { on: !!on } }))
  } catch { /* ignore */ }
}

// Subscribe to flips (for a toggle that must reflect the flag wherever it was set).
export function onLabsModeChange(fn) {
  const h = (e) => fn(!!e.detail?.on)
  window.addEventListener(EVENT, h)
  return () => window.removeEventListener(EVENT, h)
}

// Wrap a real Supabase write. `label` is a short human-readable description
// used in the dry-run console log. `fn` performs the actual write and
// should return a Supabase-shaped response (or a promise of one).
export async function labsWrite(label, fn) {
  if (!isLabsMode()) return fn()
  console.info(`[Sandbox] would write: ${label}`)
  return { data: null, error: null }
}

export const SANDBOX_RESULT = Object.freeze({ data: null, error: null, count: null, status: 200, statusText: 'OK (sandbox)' })

// A stand-in for a PostgREST/RPC builder: every chained method returns itself,
// and awaiting it resolves to SANDBOX_RESULT — so `.eq().select().single()` and
// `if (error)` at existing call sites all keep working with nothing written.
export function sandboxBuilder(label) {
  console.info(`[Sandbox] skipped write: ${label}`)
  const p = Promise.resolve(SANDBOX_RESULT)
  const chain = new Proxy(function () {}, {
    get(_, prop) {
      if (prop === 'then') return p.then.bind(p)
      if (prop === 'catch') return p.catch.bind(p)
      if (prop === 'finally') return p.finally.bind(p)
      if (prop === Symbol.toPrimitive || prop === Symbol.toStringTag) return undefined
      return () => chain
    },
    apply() { return chain },
  })
  return chain
}

// RPCs that persist or notify. Reads (get_*, *_count, leaderboards, admin
// dashboards) pass through. get_or_set_daily_* also passes: the daily games need
// their row to exist to render at all, and creating today's puzzle row is not
// user data.
const WRITE_RPC = /^(set_|mark_|log_|cast_|clear_|submit_|join_|start_|end_|edit_|report_|reveal_|unsubscribe_|admin_(ack|reopen|delete|create|set|moderate)_)/
export const isWriteRpc = (name) => WRITE_RPC.test(String(name || ''))

// Netlify functions that write, notify, or send. Everything else under
// /.netlify/functions/ (TMDB/OMDb/Nielsen lookups, AI explainers, image proxy,
// passkey ceremonies, library reads) passes through untouched.
const WRITE_FN = /\/\.netlify\/functions\/(notify-activity|unlock-skin|user-invite|invite-accept|delete-account|watch-together|webhook-token|admin-invites)(\?|$)/
export const isWriteFn = (url) => WRITE_FN.test(String(url || ''))

// Installed once at boot. Only consulted for the write-shaped function paths
// above, and only short-circuits while the flag is on — every other fetch is the
// original, untouched.
export function installSandboxGuard() {
  if (typeof window === 'undefined' || window.__nextepSandboxGuard) return
  const real = window.fetch.bind(window)
  window.__nextepSandboxGuard = true
  window.fetch = (input, init) => {
    const url = typeof input === 'string' ? input : input?.url
    if (isLabsMode() && isWriteFn(url)) {
      console.info(`[Sandbox] skipped function call: ${url}`)
      return Promise.resolve(new Response(JSON.stringify({ ok: true, sandbox: true }), { status: 200, headers: { 'Content-Type': 'application/json' } }))
    }
    return real(input, init)
  }
}
